import { Injectable } from '@angular/core';
import {SchoolClass} from "../interfaces/backend/school-class";
import {SchoolClassWithStats} from "../interfaces/backend/school-class-with-stats";
import {SchoolClassRank} from "../interfaces/backend/school-class-rank";
import {SchoolClassData} from "../interfaces/school-class-data";
import {SchoolClassDataWithStatsMin} from "../interfaces/school-class-data-with-stats-min";
import {ClassTeacher} from "../interfaces/class-teacher";

@Injectable({
  providedIn: 'root'
})
export class SchoolClassParserService {

  mapRawSchoolClass(rawSchoolClass: SchoolClass): SchoolClassData {
    return {
      id: rawSchoolClass.id,
      name: rawSchoolClass.name,
      schoolName: rawSchoolClass.school?.name ?? "",
      classCode: rawSchoolClass.classCode ?? "",
      numberStudents: rawSchoolClass.numberStudents ?? 0,
      grade: this.mapGrade(rawSchoolClass.grade),
      classNumber: rawSchoolClass.classNumber,
      classAddition: rawSchoolClass.classAddition,
      createdAt: rawSchoolClass.createdAt ?? "",
      schoolClassYear: {
        id: this.parseIntOrDefault(rawSchoolClass.schoolClassYear?.id),
        startYear: this.parseIntOrDefault(rawSchoolClass.schoolClassYear?.startYear),
        endYear: this.parseIntOrDefault(rawSchoolClass.schoolClassYear?.endYear)
      },
      teachers: (rawSchoolClass.teachers ?? []).map(teacher => this.mapTeacher(teacher))
    };
  }

  // school, createdAt and schoolClassYear are not queried in GET_SCHOOL_CLASSES_BY_SCHOOL, defaults are used
  mapRawSchoolClassWithStats(rawSchoolClass: SchoolClassWithStats): SchoolClassDataWithStatsMin {
    return {
      id: rawSchoolClass.id,
      name: rawSchoolClass.name,
      schoolName: "",
      classCode: rawSchoolClass.classCode ?? "",
      numberStudents: rawSchoolClass.numberStudents ?? 0,
      grade: this.mapGrade(rawSchoolClass.grade),
      classNumber: rawSchoolClass.classNumber,
      classAddition: rawSchoolClass.classAddition,
      createdAt: "",
      schoolClassYear: {
        id: -1,
        startYear: -1,
        endYear: -1
      },
      teachers: (rawSchoolClass.teachers ?? []).map(teacher => this.mapTeacher(teacher)),
      schoolClassRankMin: rawSchoolClass.schoolClassRankMin,
      trophyStats: rawSchoolClass.trophyStats ?? {totalCount: 0, scoredCount: 0},
      milestoneStats: rawSchoolClass.milestoneStats ?? {totalCount: 0, scoredCount: 0}
    };
  }

  // only id, name, classNumber and classAddition are queried in GET_SCHOOL_CLASS_RANKINGS, defaults are used for the rest
  mapRawRankedSchoolClass(rawSchoolClass: SchoolClassRank["schoolClass"], schoolName: string): SchoolClassData {
    return {
      id: rawSchoolClass.id,
      name: rawSchoolClass.name,
      schoolName: schoolName,
      classCode: "",
      numberStudents: 0,
      grade: this.mapGrade(null),
      classNumber: rawSchoolClass.classNumber,
      classAddition: rawSchoolClass.classAddition,
      createdAt: "",
      schoolClassYear: {
        id: -1,
        startYear: -1,
        endYear: -1
      },
      teachers: []
    };
  }

  private mapGrade(rawGrade: {id: string, name: string} | null): {id: string, name: string} {
    return {
      id: rawGrade?.id ?? "-1",
      name: rawGrade?.name ?? ""
    };
  }

  private mapTeacher(rawTeacher: {id: number | null, email?: string | null, firstname?: string | null, lastname?: string | null}): ClassTeacher {
    return {
      id: rawTeacher.id ?? -1,
      email: rawTeacher.email ?? "",
      firstname: rawTeacher.firstname ?? "",
      lastname: rawTeacher.lastname ?? ""
    };
  }

  private parseIntOrDefault(value: string | undefined): number {
    const parsed = parseInt(value ?? "");
    return isNaN(parsed) ? -1 : parsed;
  }
}
